import { axiosClient } from '@/libs/axios-client';

const API_BASE = '/payroll-automation/agent-config';

/**
 * Get all agent configurations
 * @param {Object} params - Query params (type, status, active)
 * @returns {Promise<Object>}
 */
export const getAgentConfigurations = async (params = {}) => {
  try {
    const response = await axiosClient.get(API_BASE, { params });
    return response.data;
  } catch (error) {
    console.error('Failed to get agent configurations:', error);
    throw error;
  }
};

/**
 * Get active configuration by type
 * @param {string} type - Configuration type (PCCC_PARSER, DREAM_POINT_PARSER)
 * @returns {Promise<Object>}
 */
export const getActiveConfiguration = async (type) => {
  try {
    const response = await axiosClient.get(`${API_BASE}/active/${type}`);
    return response.data;
  } catch (error) {
    console.error('Failed to get active configuration:', error);
    throw error;
  }
};

/**
 * Get agent configuration by ID
 * @param {string} id - Configuration ID
 * @returns {Promise<Object>}
 */
export const getAgentConfigurationById = async (id) => {
  try {
    const response = await axiosClient.get(`${API_BASE}/${id}`);
    return response.data;
  } catch (error) {
    console.error('Failed to get agent configuration:', error);
    throw error;
  }
};

/**
 * Create new agent configuration
 * @param {Object} data - Configuration data
 * @returns {Promise<Object>}
 */
export const createAgentConfiguration = async (data) => {
  try {
    const response = await axiosClient.post(API_BASE, data);
    return response.data;
  } catch (error) {
    console.error('Failed to create agent configuration:', error);
    throw error;
  }
};

/**
 * Update agent configuration
 * @param {string} id - Configuration ID
 * @param {Object} data - Updated configuration data
 * @returns {Promise<Object>}
 */
export const updateAgentConfiguration = async (id, data) => {
  try {
    const response = await axiosClient.put(`${API_BASE}/${id}`, data);
    return response.data;
  } catch (error) {
    console.error('Failed to update agent configuration:', error);
    throw error;
  }
};

/**
 * Activate agent configuration
 * @param {string} id - Configuration ID
 * @returns {Promise<Object>}
 */
export const activateConfiguration = async (id) => {
  try {
    const response = await axiosClient.post(`${API_BASE}/${id}/activate`);
    return response.data;
  } catch (error) {
    console.error('Failed to activate configuration:', error);
    throw error;
  }
};

/**
 * Deactivate agent configuration
 * @param {string} id - Configuration ID
 * @returns {Promise<Object>}
 */
export const deactivateConfiguration = async (id) => {
  try {
    const response = await axiosClient.post(`${API_BASE}/${id}/deactivate`);
    return response.data;
  } catch (error) {
    console.error('Failed to deactivate configuration:', error);
    throw error;
  }
};

/**
 * Test configuration with uploaded files
 * @param {string} id - Configuration ID
 * @param {FormData} formData - FormData with testFiles
 * @returns {Promise<Object>}
 */
export const testConfiguration = async (id, formData) => {
  try {
    const response = await axiosClient.post(`${API_BASE}/${id}/test`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 180000, // 3 minutes for testing
    });
    return response.data;
  } catch (error) {
    console.error('Failed to test configuration:', error);
    throw error;
  }
};

/**
 * Get configuration performance metrics
 * @param {string} id - Configuration ID
 * @param {Object} params - Query params (period)
 * @returns {Promise<Object>}
 */
export const getConfigurationMetrics = async (id, params = {}) => {
  try {
    const response = await axiosClient.get(`${API_BASE}/${id}/metrics`, { params });
    return response.data;
  } catch (error) {
    console.error('Failed to get configuration metrics:', error);
    throw error;
  }
};

/**
 * Delete agent configuration
 * @param {string} id - Configuration ID
 * @returns {Promise<Object>}
 */
export const deleteAgentConfiguration = async (id) => {
  try {
    const response = await axiosClient.delete(`${API_BASE}/${id}`);
    return response.data;
  } catch (error) {
    console.error('Failed to delete agent configuration:', error);
    throw error;
  }
};

/**
 * Clone agent configuration (creates new version with same settings and files)
 * @param {string} id - Configuration ID
 * @param {Object} data - Optional data (name, description)
 * @returns {Promise<Object>}
 */
export const cloneConfiguration = async (id, data = {}) => {
  try {
    const response = await axiosClient.post(`${API_BASE}/${id}/clone`, data);
    return response.data;
  } catch (error) {
    console.error('Failed to clone configuration:', error);
    throw error;
  }
};

/**
 * Import reference files from another configuration
 * @param {string} targetConfigId - Target configuration ID
 * @param {string} sourceConfigId - Source configuration ID
 * @param {Array<string>} fileIds - Array of file IDs to import
 * @returns {Promise<Object>}
 */
export const importReferenceFiles = async (targetConfigId, sourceConfigId, fileIds) => {
  try {
    const response = await axiosClient.post(`${API_BASE}/${targetConfigId}/import-files`, {
      sourceConfigId,
      fileIds,
    });
    return response.data;
  } catch (error) {
    console.error('Failed to import reference files:', error);
    throw error;
  }
};

const AgentConfigService = {
  getAgentConfigurations,
  getActiveConfiguration,
  getAgentConfigurationById,
  createAgentConfiguration,
  updateAgentConfiguration,
  activateConfiguration,
  deactivateConfiguration,
  testConfiguration,
  getConfigurationMetrics,
  deleteAgentConfiguration,
  cloneConfiguration,
  importReferenceFiles,
};

export default AgentConfigService;
